import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-mui";
import { keys } from "../../api/queryKeys.ts";
import { api, type ContactListParams, type MasterCompanyDetail } from "../../api/client.ts";
import AddContactDialog from "../../components/AddContactDialog.tsx";
import FilterBar from "../../components/filters/FilterBar.tsx";
import SavedViewsMenu from "../../components/filters/SavedViewsMenu.tsx";
import type { FilterSpec } from "../../components/filters/specs.ts";
import { useFilters } from "../../components/filters/useFilters.ts";
import PaginationControls from "../../components/PaginationControls.tsx";
import TableCard from "../../components/table/TableCard.tsx";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import { useTenant } from "../../contexts/TenantContext.tsx";
import { useFilterOptions } from "../../hooks/useFilterOptions.ts";
import { useUrlState } from "../../hooks/useUrlState.ts";
import { formatNumber } from "../../utils/format.ts";
import ContactsTable from "./components/ContactsTable.tsx";
import PageHeader from "../../components/PageHeader.tsx";

export default function ContactsPage() {
  const { selectedTenant } = useTenant();
  const { currentOrg } = useOrganization();

  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Keyset cursor stack: each entry is the cursor that opened that page.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);

  const url = useUrlState({ onFilterChange: () => setCursorStack([null]) });
  const { searchParams } = url;
  const name = searchParams.get("name") ?? "";
  const roleTypes = useMemo(() => searchParams.getAll("role_type"), [searchParams]);
  const companyId = searchParams.get("company_id") ?? undefined;
  const hasEmail = searchParams.get("has_email") === "true";

  // Any change of filters or page size starts over at page one — also the
  // ones that don't go through changeFilter (Back / Forward).
  const roleKey = roleTypes.join("\u0000");
  useEffect(() => {
    setCursorStack([null]);
  }, [name, roleKey, companyId, hasEmail, rowsPerPage]);

  const { data: filterOptions } = useFilterOptions();
  // The company a link narrowed to, by name when its page was just open.
  const companyName = companyId
    ? queryClient.getQueryData<MasterCompanyDetail>(keys.masterCompany.detail(companyId))?.name
    : undefined;

  const specs = useMemo<FilterSpec[]>(
    () => [
      {
        kind: "options",
        key: "role",
        label: "Role",
        param: "role_type",
        encoding: "repeat",
        pinned: true,
        loading: !filterOptions,
        options: (filterOptions?.contact_role_types ?? []).map((rt) => ({ value: rt.code, label: rt.label })),
        searchPlaceholder: "Search roles",
      },
      {
        kind: "boolean",
        key: "has_email",
        label: "Has email",
        param: "has_email",
        pinned: true,
        yesOnly: true,
        values: { yes: "true", no: "false" },
      },
      {
        kind: "custom",
        key: "company",
        label: "Company",
        params: ["company_id"],
        summary: (get) => (get("company_id") ? (companyName ?? "One company") : null),
      },
    ],
    [filterOptions, companyName],
  );
  const filters = useFilters(specs, {
    state: url,
    search: { param: "name", placeholder: "Search name" },
    pageKey: "contacts",
    scopeId: selectedTenant?.id,
    remember: true,
    // One company comes from a link; it isn't a filter to bring back.
    notRemembered: ["company_id"],
  });

  const queryParams: ContactListParams = {
    name: name || undefined,
    role_type: roleTypes.length ? roleTypes : undefined,
    company_id: companyId,
    has_email: hasEmail ? true : undefined,
    limit: rowsPerPage,
    cursor: cursorStack[cursorStack.length - 1] ?? undefined,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: keys.masterContact.list(queryParams),
    queryFn: () => api.getMasterContacts(queryParams),
    placeholderData: keepPreviousData,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <Box>
      <PageHeader
        title="Contacts"
        description={<>Find people to prospect{selectedTenant ? ` for ${selectedTenant.name}` : ""}</>}
        actions={
          <Button variant="contained" startIcon={<Plus />} onClick={() => setAddOpen(true)}>
            New contact
          </Button>
        }
      />

      {addOpen && (
        <AddContactDialog
          onClose={() => setAddOpen(false)}
          onDone={() => queryClient.invalidateQueries({ queryKey: keys.masterContact.lists() })}
        />
      )}

      <TableCard
        loading={isLoading}
        toolbar={
          <FilterBar
            filters={filters}
            count={data ? `${formatNumber(total)} ${total === 1 ? "contact" : "contacts"}` : undefined}
            views={
              <SavedViewsMenu
                filters={filters}
                context={{
                  tenantId: selectedTenant?.id,
                  tenantName: selectedTenant?.name,
                  organizationId: currentOrg?.id,
                  organizationName: currentOrg?.name,
                }}
              />
            }
            actions={isFetching && !isLoading && <CircularProgress size={20} />}
          />
        }
        footer={
          items.length > 0 && (
            <PaginationControls
              rowsPerPage={rowsPerPage}
              pageItemCount={items.length}
              total={total}
              cursorStackDepth={cursorStack.length - 1}
              hasPrev={cursorStack.length > 1}
              hasNext={Boolean(data?.next_cursor)}
              onChangeRowsPerPage={setRowsPerPage}
              onPrev={() => setCursorStack((s) => s.slice(0, -1))}
              onNext={() =>
                data?.next_cursor && setCursorStack((s) => [...s, data.next_cursor])
              }
            />
          )
        }
      >
        <ContactsTable contacts={items} filtered={filters.activeCount > 0} />
      </TableCard>
    </Box>
  );
}
